import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { MANUAL_REQUEST_UPLOAD } from '../../constants/constant';
import { downloadManualRequestErrors, uploadManualRequests } from '../../utils/api-service';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const OUTCOMES = ['COMPLETED', 'PARTIAL_SUCCESS', 'NO_VALID_ROWS', 'PROCESSING_FAILED'];
const RESULT_GROUPS = ['savedRows', 'rejectedRows', 'unprocessedRows', 'unknownRows'];

function isUploadResult(result) {
  if (!result || !OUTCOMES.includes(result.outcome) || typeof result.correlationId !== 'string') return false;
  const counts = ['totalRows', 'savedCount', 'rejectedCount', 'unprocessedCount', 'unknownCount'];
  if (!counts.every((field) => Number.isInteger(result[field]) && result[field] >= 0)) return false;
  if (result.totalRows > 500 || result.totalRows !== result.savedCount + result.rejectedCount
    + result.unprocessedCount + result.unknownCount) return false;
  if (!RESULT_GROUPS.every((field) => Array.isArray(result[field]))) return false;
  if (!RESULT_GROUPS.every((field, index) => result[field].length === result[counts[index + 1]])) return false;
  if (result.outcome !== 'PROCESSING_FAILED' && (result.unprocessedCount > 0 || result.unknownCount > 0)) return false;
  if (result.outcome === 'COMPLETED' && (result.savedCount === 0 || result.rejectedCount !== 0)) return false;
  if (result.outcome === 'PARTIAL_SUCCESS' && (result.savedCount === 0 || result.rejectedCount === 0)) return false;
  if (result.outcome === 'NO_VALID_ROWS' && (result.savedCount !== 0 || result.rejectedCount === 0)) return false;
  if (result.reportStatus === 'AVAILABLE' && !result.errorWorkbook) return false;
  return ['NOT_REQUIRED', 'AVAILABLE', 'FAILED'].includes(result.reportStatus);
}

function summaryMessage(data) {
  const counts = `${data.savedCount} added successfully, ${data.rejectedCount} failed`;
  if (data.outcome === 'PROCESSING_FAILED') {
    const stopped = `${data.unprocessedCount} not processed${data.unknownCount ? `, ${data.unknownCount} to check in the list` : ''}`;
    return `${data.message || 'Processing stopped.'} (${counts}, ${stopped}.)`;
  }
  const download = data.reportStatus === 'AVAILABLE'
    ? ' The result file has been downloaded; see the Status and Result columns for each row.'
    : ' The result file could not be generated; check the Manual Request list.';
  return `Upload complete: ${counts}.${download}`;
}

const ManualRequestUpload = ({ onSaved, onNotify }) => {
  const fileInput = useRef(null);
  const inFlight = useRef(false);
  const [uploading, setUploading] = useState(false);

  const downloadReport = (report) => {
    try {
      downloadManualRequestErrors(report);
      return true;
    } catch (error) {
      console.error('Could not download manual request upload result', error);
      onNotify({ severity: 'error', message: 'Could not download the upload result file. Please try again.' });
      return false;
    }
  };

  const refreshList = async () => {
    try {
      if (await onSaved() === false) {
        onNotify({ severity: 'warning', message: 'Upload finished, but the list could not be refreshed. Reload the page to see new requests.' });
      }
    } catch (error) {
      console.error('Could not refresh manual requests after upload', error);
    }
  };

  const handleResult = async (data) => {
    const report = data.reportStatus === 'AVAILABLE' ? data.errorWorkbook : null;
    if (report) {
      downloadReport(report);
    }
    const severity = data.outcome === 'COMPLETED' ? 'success'
      : data.outcome === 'PARTIAL_SUCCESS' ? 'warning' : 'error';
    onNotify({ severity, message: summaryMessage(data) });
    if (data.savedCount > 0 || data.unknownCount > 0) {
      await refreshList();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || inFlight.current) return;
    if (!/\.xlsx$/i.test(file.name) || file.size === 0 || file.size > MAX_FILE_BYTES) {
      onNotify({ severity: 'error', message: 'Select a non-empty .xlsx file no larger than 5 MiB (500 data rows maximum).' });
      return;
    }
    inFlight.current = true;
    setUploading(true);
    try {
      const response = await uploadManualRequests(MANUAL_REQUEST_UPLOAD, file);
      if (response.status !== 200 || !isUploadResult(response.data)) {
        throw new Error('The server returned an unrecognized upload result.');
      }
      await handleResult(response.data);
    } catch (error) {
      const data = error.response?.data;
      if (isUploadResult(data) && data.outcome === 'PROCESSING_FAILED') {
        await handleResult(data);
      } else {
        console.error('Manual request upload failed', error);
        const status = error.response?.status;
        const message = status === 413 ? 'The upload exceeds the allowed file size.'
          : status === 401 || status === 403 ? 'You are not authorized to upload manual requests.'
            : status === 400 ? data?.message || 'The workbook is invalid. Download the latest template and try again.'
              : 'Upload outcome is unconfirmed. Check the manual request list before retrying; some rows may already have been saved.';
        onNotify({ severity: 'error', message });
        if (!status || status >= 500) {
          await refreshList();
        }
      }
    } finally {
      inFlight.current = false;
      setUploading(false);
    }
  };

  return (
    <>
      <input
        ref={fileInput}
        type="file"
        accept=".xlsx"
        aria-label="Select manual request workbook"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={uploading}
      />
      <Button
        variant="outlined"
        startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
        onClick={() => fileInput.current.click()}
        disabled={uploading}
        aria-busy={uploading}
      >
        {uploading ? 'Processing...' : 'Upload Template'}
      </Button>
    </>
  );
};

ManualRequestUpload.propTypes = {
  onSaved: PropTypes.func.isRequired,
  onNotify: PropTypes.func.isRequired,
};

export default ManualRequestUpload;